import React, { useState, useEffect } from "react";
import { Loader2, CheckCircle2, Truck } from "lucide-react";
import api from "../lib/api";

const PLANS = [
  { v: "Basic ($250–$300)", d: "Basic starter website with essential pages and contact form." },
  { v: "Standard ($650–$900)", d: "Multi-page professional site with enhanced design and features." },
  { v: "Premium ($1300–$2000)", d: "Advanced trucking website with custom features, integrations, and load board options." },
  { v: "Enterprise ($2000–$3000)", d: "Fully custom solution with advanced functionality, automation, and scalable systems." },
];

const BUSINESS_DEFAULT = ["Carrier", "Owner Operator", "Dispatch Company", "Freight Broker", "Logistics Company"];
const STYLES = ["Professional", "Modern", "Heavy-Duty Trucking", "Luxury/Premium"];
const PAGES_DEFAULT = ["Home", "About Us", "Services", "Equipment/Fleet", "Contact", "Careers", "Load Board"];
const EQUIP_DEFAULT = ["Dry Van", "Reefer", "Flatbed", "Step Deck", "Power Only", "Heavy Haul", "Box Truck"];
const HIRING = ["CDL Drivers", "Owner Operators", "Dispatchers"];
const CONTENT = ["Logo", "Truck photos", "Company photos", "Services offered", "About company information", "Social media links"];
const FEATURES_DEFAULT = ["Quote Request Form", "Carrier Application", "Customer Contact Form", "Testimonials"];

const empty = {
  plan: "", company_name: "", contact_name: "", contact_info: "",
  business_types: [], business_other: "", has_logo: "", color_primary: "", color_secondary: "",
  style: "", pages: [], display_equipment: "", equipment_types: [], need_load_board: "",
  need_freight_posting: "", careers_page: "", hiring_for: [], application_type: "",
  content_provided: [], additional_features: [], notes: "",
};

const Chk = ({ label, checked, onChange }) => (
  <label className={`intake-chk ${checked ? "on" : ""}`}>
    <input type="checkbox" checked={checked} onChange={onChange} />
    <span>{label}</span>
  </label>
);

const Radio = ({ name, label, value, current, onChange }) => (
  <label className={`intake-chk ${current === value ? "on" : ""}`}>
    <input type="radio" name={name} checked={current === value} onChange={() => onChange(value)} />
    <span>{label}</span>
  </label>
);

const WebsiteIntake = () => {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [cfg, setCfg] = useState(null);

  useEffect(() => { api.get("/content").then((r) => setCfg(r.data?.intake || null)).catch(() => {}); }, []);

  const BUSINESS = cfg?.business_types || BUSINESS_DEFAULT;
  const EQUIP = cfg?.equipment || EQUIP_DEFAULT;
  const PAGES = cfg?.pages || PAGES_DEFAULT;
  const FEATURES = cfg?.features || FEATURES_DEFAULT;

  const toggle = (field, val) => {
    setForm((f) => {
      const arr = f[field].includes(val) ? f[field].filter((x) => x !== val) : [...f[field], val];
      return { ...f, [field]: arr };
    });
  };
  const set = (field, val) => setForm((f) => ({ ...f, [field]: val }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/website-intake", form);
      setDone(true);
      window.scrollTo({ top: document.getElementById("website-intake").offsetTop - 80, behavior: "smooth" });
    } catch (err) { alert("Something went wrong. Please try again."); }
    finally { setBusy(false); }
  };

  if (done) {
    return (
      <section className="intake" id="website-intake">
        <div className="container">
          <div className="intake-done">
            <CheckCircle2 size={54} color="#3fae4a" />
            <h2>Intake received!</h2>
            <p>Thanks {form.contact_name || ""}! We've got your website project details and will reach out with a quote and next steps. You can also call 815-406-9774.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="intake" id="website-intake">
      <div className="container">
        <div className="section-head">
          <span className="kicker"><Truck size={13} style={{ verticalAlign: "-2px" }} /> Website Client Intake</span>
          <h2>{cfg?.title || "Website builder intake form"}</h2>
          <p>{cfg?.description || "Tell us about your trucking website project and we'll build something that represents your business. Coding is provided; adding a domain may be extra unless already purchased."}</p>
        </div>

        <form className="intake-form" onSubmit={submit}>
          {/* Plan */}
          <div className="intake-block">
            <h3>Select your website plan</h3>
            <div className="intake-plans">
              {PLANS.map((p) => (
                <label key={p.v} className={`intake-plan ${form.plan === p.v ? "on" : ""}`}>
                  <input type="radio" name="plan" checked={form.plan === p.v} onChange={() => set("plan", p.v)} />
                  <span className="intake-plan-title">{p.v}</span>
                  <span className="intake-plan-desc">{p.d}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Company info */}
          <div className="intake-block">
            <h3>Company information</h3>
            <div className="form-row">
              <div className="field"><label>Company Name</label><input required value={form.company_name} onChange={(e) => set("company_name", e.target.value)} /></div>
              <div className="field"><label>Contact Name</label><input required value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} /></div>
            </div>
            <div className="field"><label>Phone / Email</label><input required value={form.contact_info} onChange={(e) => set("contact_info", e.target.value)} placeholder="Phone and/or email" /></div>
            <label className="intake-sublabel">Business Type</label>
            <div className="intake-grid">
              {BUSINESS.map((b) => <Chk key={b} label={b} checked={form.business_types.includes(b)} onChange={() => toggle("business_types", b)} />)}
            </div>
            <div className="field"><label>Other business type</label><input value={form.business_other} onChange={(e) => set("business_other", e.target.value)} /></div>
          </div>

          {/* Branding */}
          <div className="intake-block">
            <h3>Branding & design</h3>
            <label className="intake-sublabel">Do you have a logo?</label>
            <div className="intake-grid">
              <Radio name="logo" label="Yes (I'll send the file)" value="Yes" current={form.has_logo} onChange={(v) => set("has_logo", v)} />
              <Radio name="logo" label="No (logo design available)" value="No" current={form.has_logo} onChange={(v) => set("has_logo", v)} />
            </div>
            <div className="form-row">
              <div className="field"><label>Primary brand color</label><input value={form.color_primary} onChange={(e) => set("color_primary", e.target.value)} placeholder="e.g. Navy" /></div>
              <div className="field"><label>Secondary brand color</label><input value={form.color_secondary} onChange={(e) => set("color_secondary", e.target.value)} placeholder="e.g. Green" /></div>
            </div>
            <label className="intake-sublabel">Preferred style</label>
            <div className="intake-grid">
              {STYLES.map((s) => <Radio key={s} name="style" label={s} value={s} current={form.style} onChange={(v) => set("style", v)} />)}
            </div>
          </div>

          {/* Pages */}
          <div className="intake-block">
            <h3>Pages needed</h3>
            <div className="intake-grid">
              {PAGES.map((p) => <Chk key={p} label={p} checked={form.pages.includes(p)} onChange={() => toggle("pages", p)} />)}
            </div>
          </div>

          {/* Trucking features */}
          <div className="intake-block">
            <h3>Trucking & freight features</h3>
            <label className="intake-sublabel">Display your trucks/equipment?</label>
            <div className="intake-grid">
              <Radio name="disp" label="Yes" value="Yes" current={form.display_equipment} onChange={(v) => set("display_equipment", v)} />
              <Radio name="disp" label="No" value="No" current={form.display_equipment} onChange={(v) => set("display_equipment", v)} />
            </div>
            <label className="intake-sublabel">Equipment types</label>
            <div className="intake-grid">
              {EQUIP.map((eq) => <Chk key={eq} label={eq} checked={form.equipment_types.includes(eq)} onChange={() => toggle("equipment_types", eq)} />)}
            </div>
            <div className="form-row">
              <div className="field"><label>Need a load board?</label>
                <select value={form.need_load_board} onChange={(e) => set("need_load_board", e.target.value)}><option value="">Select</option><option>Yes</option><option>No</option></select>
              </div>
              <div className="field"><label>Freight/truck posting?</label>
                <select value={form.need_freight_posting} onChange={(e) => set("need_freight_posting", e.target.value)}><option value="">Select</option><option>Yes</option><option>No</option></select>
              </div>
            </div>
          </div>

          {/* Careers */}
          <div className="intake-block">
            <h3>Careers & applications</h3>
            <label className="intake-sublabel">Want a job opportunities page?</label>
            <div className="intake-grid">
              <Radio name="careers" label="Yes" value="Yes" current={form.careers_page} onChange={(v) => set("careers_page", v)} />
              <Radio name="careers" label="No" value="No" current={form.careers_page} onChange={(v) => set("careers_page", v)} />
            </div>
            <label className="intake-sublabel">Hiring for</label>
            <div className="intake-grid">
              {HIRING.map((h) => <Chk key={h} label={h} checked={form.hiring_for.includes(h)} onChange={() => toggle("hiring_for", h)} />)}
            </div>
            <div className="field"><label>Application type</label>
              <select value={form.application_type} onChange={(e) => set("application_type", e.target.value)}><option value="">Select</option><option>Online Form</option><option>Email</option><option>Phone</option></select>
            </div>
          </div>

          {/* Content & additional */}
          <div className="intake-block">
            <h3>Content & additional requests</h3>
            <label className="intake-sublabel">Content you can provide</label>
            <div className="intake-grid">
              {CONTENT.map((c) => <Chk key={c} label={c} checked={form.content_provided.includes(c)} onChange={() => toggle("content_provided", c)} />)}
            </div>
            <label className="intake-sublabel">Features needed</label>
            <div className="intake-grid">
              {FEATURES.map((f) => <Chk key={f} label={f} checked={form.additional_features.includes(f)} onChange={() => toggle("additional_features", f)} />)}
            </div>
            <div className="field"><label>Notes</label><textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Anything else we should know..."></textarea></div>
          </div>

          <button className="btn btn-primary btn-block" disabled={busy}>{busy ? <Loader2 size={16} className="spin" /> : null} Submit Website Intake</button>
        </form>
      </div>
    </section>
  );
};

export default WebsiteIntake;
